import React, { ReactElement, useState } from 'react';
import moment, { Moment } from 'moment';
import { LoadingDialog } from 'src/web/components/Modals/LoadingDialog';
import { DateTimePicker } from 'src/web/components/DateTimePicker/DateTimePicker';
import {
  UserSubscriptionStripeConnectStatus,
  RevenueShare,
  TAX_LABEL,
  UserSubscriptionPlanPaymentTransactions,
} from '../../../../ReduxStorage/UserSubscriptionPlans/constants';
import { alertDialog as alert } from '../../../components/Modals/AlertDialog';
import { AlertMessage } from '../../../components/AlertMessage/component';
import { Chip } from '../../../components/Chip/component';
import { Select } from '../../../components/Select/component';
import {
  getChipVariantFromStripeConnectStatus,
  getChipVariantFromPaymentTransactionStatus,
  makeCountryOptions,
  exportPaymentTranToCsv,
} from './helpers';
import { StripeConnectCountries } from './constants';
import { capitalizeFirstLetter } from '../../../utilities/utilities';
import { UserIcon, UserRow } from '../ManageUsers/styles';
import {
  ActionsContainer,
  BannerContainer,
  BannerLinkStyle,
  ExportContainer,
  Header,
  Heading,
  InputContainer,
  NeedHelpLinkStyle,
  SelectErrorContainer,
  StyledButton,
  StyledTable,
} from './styles';
import { ActionButton } from '../../../components/Buttons/ActionButton/ActionButton';
import {
  ROUTE_CONTACT_PAGE,
  ROUTE_COURSE_PAGE,
  ROUTE_USER_PROFILE_PAGE,
} from '../../../../ReduxStorage/routeActions';
import { RouteLink } from '../../../components/RouteLink/component';
import { SubscriptionPlanType } from '../../../../ReduxStorage/SubscriptionPlans/constants';
import { Paginate } from '../../../components/Pagination/Paginate';
import { CursorPageSize } from '../../../components/Pagination/types';
import {
  courseCodeFromPath,
  institutionPrefixFromPath,
} from '../../../../ReduxStorage/helpers/paths';

export type CourseMarketPlaceSetupProps = {
  planType: SubscriptionPlanType;
  accountStatus: UserSubscriptionStripeConnectStatus;
  onSetupStripeAccountClick: (country: string) => Promise<void>;
};

const MarketPlaceDisabledAlert = (): ReactElement => (
  <AlertMessage
    kind="info"
    title="Revenue sharing is off"
    message={
      <span>
        Please reach out to{' '}
        <BannerLinkStyle href="mailto:support@openlearning.com">
          support@openlearning.com
        </BannerLinkStyle>{' '}
        to discuss enabling revenue sharing for your Marketplace courses.
      </span>
    }
  />
);

const NoStripeAccountAlert = (): ReactElement => (
  <AlertMessage
    kind="info"
    title="Set up your Stripe Connect account"
    message="To get paid for your courses that are listed on the marketplace for both learner enrolment and paid certification, please create a Stripe connect account."
  />
);

const COUNTRY_OPTIONS = makeCountryOptions();

const CourseMarketplaceSetup = ({
  accountStatus,
  onSetupStripeAccountClick,
}: CourseMarketPlaceSetupProps): ReactElement => {
  const [country, setCountry] = useState('');
  const disabled = accountStatus === 'no_revenue_share';
  return (
    <div>
      <BannerContainer>
        {disabled ? <MarketPlaceDisabledAlert /> : <NoStripeAccountAlert />}
      </BannerContainer>
      <InputContainer>
        <Select
          label="Country"
          description="Requires you to have a bank account in this country"
          value={country}
          options={COUNTRY_OPTIONS}
          onValueChange={setCountry}
          required
          disabled={disabled}
        />
        {accountStatus === 'no_stripe_account' &&
          country !== '' &&
          !StripeConnectCountries.includes(country) && (
            <SelectErrorContainer>
              Unfortunately Stripe Connect is currently available in your
              country and the payout process will be done manually. To review
              your course marketplace revenue sharing and payment terms, please{' '}
              <a href="mailto:support@openlearning.com">contact us</a>.
            </SelectErrorContainer>
          )}
      </InputContainer>
      <ActionsContainer>
        <ActionButton
          disabled={disabled || !StripeConnectCountries.includes(country)}
          onClick={() => onSetupStripeAccountClick(country)}
          colour="kelp"
          label="Set up your stripe account"
        />
      </ActionsContainer>
      <div>
        <span>
          Have a question about how Stripe Connect works?{' '}
          <NeedHelpLinkStyle href="mailto:support@openlearning.com">
            Contact us
          </NeedHelpLinkStyle>
        </span>
      </div>
    </div>
  );
};

export type CourseMarketplaceManagerProps = {
  subscriptionId: string;
  accountStatus: UserSubscriptionStripeConnectStatus;
  paymentTransactions: UserSubscriptionPlanPaymentTransactions;
  goToStripeConnect: () => Promise<void>; // the link
  fetchSubscriptionPaymentTransactions: (
    subscriptionId: string,
    afterPaymentId?: string,
    limit?: number,
    page?: number
  ) => Promise<void>;
  revenueShare: RevenueShare;
};

const DEFAULT_PAGE_NUMBER = 1;
const DEFAULT_PAGE_SIZE = 10;

const CourseMarketplaceManager = ({
  subscriptionId,
  accountStatus,
  paymentTransactions,
  goToStripeConnect,
  fetchSubscriptionPaymentTransactions,
  revenueShare,
}: CourseMarketplaceManagerProps): ReactElement => {
  const [fromDate, setFromDate] = useState(moment().subtract(1, 'month'));
  const [toDate, setToDate] = useState(moment());
  const [exporting, setExporting] = useState(false);
  const [paymentsPageNumber, setPaymentsPageNumber] = useState(
    DEFAULT_PAGE_NUMBER
  );
  const [paymentsPageSize, setPaymentsPageSize] = useState(DEFAULT_PAGE_SIZE);

  const handlePageSizeChange = (pageSize: CursorPageSize): void => {
    fetchSubscriptionPaymentTransactions(
      subscriptionId,
      undefined,
      Number(pageSize),
      1
    );
    setPaymentsPageSize(pageSize);
    setPaymentsPageNumber(1);
  };

  const handlePageChange = (newPage: number): void => {
    if (!paymentTransactions.results[newPage]) {
      const currentPaymentsPageList =
        paymentTransactions.results[paymentsPageNumber];
      const afterPaymentId =
        currentPaymentsPageList[currentPaymentsPageList.length - 1].id;

      fetchSubscriptionPaymentTransactions(
        subscriptionId,
        afterPaymentId,
        Number(paymentsPageSize),
        newPage
      );
    }

    setPaymentsPageNumber(newPage);
  };

  const exportToCsv = async (): Promise<void> => {
    if (toDate.diff(fromDate, 'months') > 12) {
      alert({
        title: 'error',
        message:
          'Can only export less than one year period of payment transactions',
      });
      return;
    }
    setExporting(true);
    await exportPaymentTranToCsv(subscriptionId, fromDate, toDate);
    setExporting(false);
  };

  const { total, results: paymentTransactionsPaginated } = paymentTransactions;

  return (
    <div>
      <StyledTable>
        <tr>
          {!revenueShare.useOlConnect && <th>Stripe Connect Account</th>}
          <th>Processing fee</th>
          <th>Taxation</th>
          <th>Payout cycle</th>
          <th>Actions</th>
        </tr>
        <tr>
          {!revenueShare.useOlConnect && (
            <td>
              <Chip
                variant={getChipVariantFromStripeConnectStatus(accountStatus)}
                label={capitalizeFirstLetter(accountStatus)}
              />
            </td>
          )}
          <td>
            {revenueShare.processingFees
              ? 'Yes - deducted automatically '
              : 'No - absorbed by OpenLearning'}
          </td>
          <td>{TAX_LABEL[revenueShare.tax]}</td>
          <td>{revenueShare.useOlConnect ? 'Monthly' : 'Weekly'}</td>
          <td>
            {revenueShare.useOlConnect ? (
              <RouteLink
                to={{
                  type: ROUTE_CONTACT_PAGE,
                }}
              >
                Contact us
              </RouteLink>
            ) : (
              <StyledButton
                variant="legacy-kelp"
                action={{
                  kind: 'on_click',
                  value: () => goToStripeConnect(),
                }}
              >
                Go to Stripe Connect
              </StyledButton>
            )}
          </td>
        </tr>
      </StyledTable>
      <div>
        <Heading className="below-this" id="below-this">
          Payment transaction history
        </Heading>
        <ExportContainer>
          <DateTimePicker
            label="From date"
            value={fromDate}
            disableClear
            blockManualInput
            dateFormat="DD MMM YYYY"
            timeFormat={false}
            onChange={(selectedDate) => {
              if (typeof selectedDate !== 'string') setFromDate(selectedDate);
            }}
            isValidDate={(current: Moment): boolean =>
              current.isSameOrBefore(moment().subtract(1, 'day'))
            }
            closeOnSelect
          />
          &nbsp;&nbsp;&nbsp;
          <DateTimePicker
            label="To date"
            disableClear
            value={toDate}
            blockManualInput
            dateFormat="DD MMM YYYY"
            timeFormat={false}
            onChange={(selectedDate) => {
              if (typeof selectedDate !== 'string') setToDate(selectedDate);
            }}
            isValidDate={(current: Moment): boolean =>
              current.isAfter(fromDate) && current.isSameOrBefore(moment())
            }
            closeOnSelect
          />
          &nbsp;&nbsp;&nbsp;
          <StyledButton
            variant="legacy-kelp"
            action={{
              kind: 'on_click',
              value: exportToCsv,
            }}
          >
            Export
          </StyledButton>
        </ExportContainer>
        <StyledTable>
          <thead>
            <tr>
              <th>Item</th>
              <th>Learner</th>
              <th>Amount you receive {/* Add info tip here */}</th>
              <th>Stripe fees {/* Add info tip here */}</th>
              <th>Revenue share with OL</th>
              <th>Purchase Date</th>
              <th>Payout Date</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {(paymentTransactionsPaginated[paymentsPageNumber] || []).map(
              (transaction) => {
                const courseCode = courseCodeFromPath(transaction.coursePath);
                return (
                  <tr>
                    <td>
                      {courseCode ? (
                        <RouteLink
                          to={{
                            type: ROUTE_COURSE_PAGE,
                            params: {
                              courseCode,
                            },
                            query: {},
                            state: {
                              institutionPrefix:
                                institutionPrefixFromPath(
                                  transaction.coursePath
                                ) || undefined,
                            },
                          }}
                        >
                          {transaction.item}
                        </RouteLink>
                      ) : (
                        transaction.item
                      )}
                    </td>
                    <td>
                      <UserRow>
                        <UserIcon src={transaction.user.smallAvatar} />
                        <RouteLink
                          to={{
                            type: ROUTE_USER_PROFILE_PAGE,
                            params: {
                              profileName: transaction.user.profileName,
                            },
                          }}
                        >
                          {transaction.user.fullName}
                        </RouteLink>
                      </UserRow>
                    </td>
                    <td>{`$${transaction.amountReceived} ${transaction.settledCurrency}`}</td>
                    <td>{`$${
                      Math.round(
                        (transaction.stripeFee + transaction.payoutFee) * 100
                      ) / 100
                    } ${transaction.settledCurrency}`}</td>
                    <td>{`$${transaction.revenueShare} ${transaction.settledCurrency}`}</td>
                    <td>
                      {transaction.purchaseDate &&
                        moment(transaction.purchaseDate).format('DD/MM/YYYY')}
                    </td>
                    <td>
                      {transaction.payoutDate &&
                        moment(transaction.payoutDate).format('DD/MM/YYYY')}
                    </td>
                    <td>
                      <Chip
                        variant={getChipVariantFromPaymentTransactionStatus(
                          transaction.status
                        )}
                        label={capitalizeFirstLetter(transaction.status)}
                      />
                    </td>
                  </tr>
                );
              }
            )}
          </tbody>
        </StyledTable>
        <Paginate
          totalPages={Math.ceil(total / paymentsPageSize)}
          page={paymentsPageNumber}
          pageSize={paymentsPageSize}
          totalResults={total}
          handlePageSizeChange={handlePageSizeChange}
          handlePageChange={handlePageChange}
          pageSizeOptions={[10, 25, 50, 100]}
          showPageSizeSelector
        />
      </div>
      <LoadingDialog
        show={exporting}
        heading="Exporting"
        body="Please wait while we're exporting. Do not click back or close this browser window"
      />
    </div>
  );
};

export type CourseMarketplaceProps = {
  subscriptionId: string;
  planType: SubscriptionPlanType;
  accountStatus: UserSubscriptionStripeConnectStatus;
  paymentTransactions: UserSubscriptionPlanPaymentTransactions;
  goToStripeConnect: () => Promise<void>;
  revenueShare: RevenueShare | null;
  onSetupStripeAccountClick: (country: string) => Promise<void>;
  fetchSubscriptionPaymentTransactions: (
    subscriptionId: string,
    afterPaymentId?: string,
    limit?: number,
    page?: number
  ) => Promise<void>;
};

export const CourseMarketplace = ({
  subscriptionId,
  planType,
  accountStatus,
  paymentTransactions,
  goToStripeConnect,
  revenueShare,
  onSetupStripeAccountClick,
  fetchSubscriptionPaymentTransactions,
}: CourseMarketplaceProps): ReactElement => (
  <div>
    <Header>
      <Heading>Course marketplace</Heading>
    </Header>
    {accountStatus === 'no_revenue_share' ||
    accountStatus === 'ol_revenue_no_stripe_account' ||
    (accountStatus === 'no_stripe_account' && !revenueShare?.useOlConnect) ? (
      <CourseMarketplaceSetup
        {...{
          planType,
          accountStatus,
          onSetupStripeAccountClick,
        }}
      />
    ) : revenueShare ? (
      <CourseMarketplaceManager
        {...{
          subscriptionId,
          revenueShare,
          accountStatus,
          paymentTransactions,
          goToStripeConnect,
          fetchSubscriptionPaymentTransactions,
        }}
      />
    ) : null}
  </div>
);
